/**
 * CUSTOMIZATION: MVP 2.1.11 / 3.1.4 — client-side quote PDF matching Presidio ERP order chrome
 * (logo, geometric watermark, lime accents, address/totals layout). Pure TypeScript; Helvetica + JPEG XObjects.
 */

import {
  PRESIDIO_QUOTE_PDF_LOGO,
  PRESIDIO_QUOTE_PDF_WATERMARK,
  decodeJpegBase64,
} from './presidio-quote-pdf-assets';

export interface PresidioQuotePdfLine {
  lineNumber: number;
  /** Internal SKU when available (MyOrders / enrichment). */
  sku?: string;
  /** Buyer-facing manufacturer part # when available. */
  partNumber: string;
  description: string;
  quantity: number;
  unitPrice?: number;
  extPrice?: number;
  manufacturer?: string;
  startDate?: string;
  endDate?: string;
}

/** Address block printed on the quote PDF (MVP 2.1.11 confirm step). */
export interface PresidioQuotePdfAddress {
  label: 'Bill to' | 'Ship to' | 'End User';
  company?: string;
  name?: string;
  lines?: string[];
  cityStateZip?: string;
  phone?: string;
  email?: string;
}

export interface PresidioQuotePdfDocument {
  quoteNumber: string;
  title: string;
  sourceLabel: string;
  createdAt: Date;
  accountName?: string;
  currency?: string;
  lines: PresidioQuotePdfLine[];
  subtotal?: number;
  notes?: string;
  billTo?: PresidioQuotePdfAddress;
  shipTo?: PresidioQuotePdfAddress;
  /** Defaults to ship-to when omitted. */
  endUser?: PresidioQuotePdfAddress;
  /** Additional unique ship-tos when quote uses split shipping (MVP 2.6.2). */
  additionalShipTos?: PresidioQuotePdfAddress[];
  customerNumber?: string;
  customerPo?: string;
  accountManager?: string;
  insideSalesRep?: string;
  paymentTerms?: string;
}

const PAGE_W = 612; // US Letter
const PAGE_H = 792;
const MARGIN_X = 35;
const MARGIN_BOTTOM = 52;
const LIME = { r: 0.698, g: 0.733, b: 0.118 }; // ~#B2BB1E
const GRAY_TOTAL = { r: 0.827, g: 0.827, b: 0.827 };
const FOOTER_URL = 'https://www.presidio.com';
const FOOTER_CONFIDENTIAL =
  'This document may contain confidential and proprietary information belonging to Presidio Networked Solutions LLC. and/or its affiliates and subsidiaries. It is intended for the use of the recipient only. Any unauthorized disclosure or distribution is prohibited.';

function escapePdf(text: string): string {
  const ascii = (text || '').replace(/[^\x20-\x7E]/g, '?');
  return ascii.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

function formatMoney(value: number | undefined, currency = 'USD'): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

function truncate(text: string, max: number): string {
  const t = (text || '').trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}?`;
}

function formatDateMdY(d: Date): string {
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

/**
 * Build a PDF 1.4 document matching Presidio ERP order chrome for cart-generated quotes.
 */
export function buildPresidioQuotePdf(doc: PresidioQuotePdfDocument): Uint8Array {
  const currency = doc.currency || 'USD';
  const dateStr = formatDateMdY(doc.createdAt);
  const subtotal =
    doc.subtotal ?? doc.lines.reduce((sum, l) => sum + (l.extPrice != null ? l.extPrice : 0), 0);

  // Pass 1: layout body pages (no chrome) to know page count
  const bodyPages = layoutBodyPages(doc, currency, subtotal);
  const pageCount = Math.max(1, bodyPages.length);

  // Pass 2: prepend chrome + footer on each page
  const contentPages = bodyPages.map((body, idx) => {
    const ops: string[] = [];
    drawPageChrome(ops, doc, dateStr, idx + 1, pageCount);
    ops.push(body);
    drawFooter(ops);
    return ops.join('\n');
  });

  return assemblePdf(contentPages);
}

function layoutBodyPages(doc: PresidioQuotePdfDocument, currency: string, subtotal: number): string[] {
  const pages: string[] = [];
  let ops: string[] = [];
  // After header chrome, content starts ~ below addresses region on page 1
  let y = PAGE_H - 300;
  let isFirstPage = true;

  const flush = () => {
    pages.push(ops.join('\n'));
    ops = [];
    isFirstPage = false;
    y = PAGE_H - 110; // below header on continuation pages
  };

  const ensureSpace = (needed: number) => {
    if (y - needed < MARGIN_BOTTOM + 8) {
      flush();
    }
  };

  // Address + meta only on first page
  y = drawAddressesAndMeta(ops, doc, PAGE_H - 100);
  ensureSpace(40);

  // Table header
  ensureSpace(28);
  y = drawTableHeader(ops, y);
  y -= 6;

  if (!doc.lines.length) {
    ensureSpace(16);
    ops.push(textAt(MARGIN_X + 8, y, 8, 'No line items.'));
    y -= 16;
  }

  for (const line of doc.lines) {
    const descFull = [line.manufacturer, line.description].filter(Boolean).join(' · ') || line.description || '';
    const descLines = wrapText(descFull, 62);
    const subRows = (line.startDate ? 1 : 0) + (line.endDate ? 1 : 0);
    const blockH = 12 + Math.max(0, descLines.length - 1) * 10 + subRows * 11 + 8;
    ensureSpace(blockH);
    if (!isFirstPage && ops.length === 0) {
      y = drawTableHeader(ops, y);
      y -= 6;
    }

    const rowTop = y;
    ops.push(textAt(MARGIN_X + 8, y, 7, String(line.lineNumber)));
    ops.push(textAt(MARGIN_X + 22, y, 7, truncate(line.partNumber || '—', 18)));
    ops.push(textAt(MARGIN_X + 106, y, 7, truncate(descLines[0] || '', 62)));
    ops.push(textAt(MARGIN_X + 400, y, 7, String(line.quantity)));
    ops.push(textAt(MARGIN_X + 440, y, 7, formatMoney(line.unitPrice, currency)));
    ops.push(textAt(MARGIN_X + 510, y, 7, formatMoney(line.extPrice, currency)));
    y -= 10;
    for (let i = 1; i < descLines.length; i++) {
      ops.push(textAt(MARGIN_X + 106, y, 7, truncate(descLines[i], 62)));
      y -= 10;
    }
    if (line.startDate) {
      ops.push(textAt(MARGIN_X + 92, y, 7, 'Start Date:', true));
      ops.push(textAt(MARGIN_X + 134, y, 7, line.startDate));
      y -= 11;
    }
    if (line.endDate) {
      ops.push(textAt(MARGIN_X + 92, y, 7, 'End Date:', true));
      ops.push(textAt(MARGIN_X + 134, y, 7, line.endDate));
      y -= 11;
    }
    y -= 4;
    // row rule
    ops.push(`${MARGIN_X} ${y + 2} m ${PAGE_W - MARGIN_X} ${y + 2} l 0.5 0.5 0.5 RG 0.34 w S`);
    y -= 4;
    void rowTop;
  }

  // Totals
  ensureSpace(48);
  y -= 8;
  y = drawTotals(ops, y, subtotal, currency);

  if (doc.notes) {
    ensureSpace(24);
    ops.push(textAt(MARGIN_X, y, 7, truncate(doc.notes, 100)));
    y -= 12;
  }

  if (doc.additionalShipTos?.length) {
    ensureSpace(40);
    ops.push(textAt(MARGIN_X, y, 8, 'Additional ship-to addresses:', true));
    y -= 12;
    for (const extra of doc.additionalShipTos) {
      ensureSpace(36);
      y = drawAddressColumn(ops, extra, MARGIN_X, y, 240, false);
      y -= 6;
    }
  }

  pages.push(ops.join('\n'));
  return pages.filter(p => p.length >= 0);
}

function drawAddressesAndMeta(ops: string[], doc: PresidioQuotePdfDocument, startY: number): number {
  let y = startY;

  // Lime rules under header
  ops.push(strokeLine(MARGIN_X, y + 8, PAGE_W - MARGIN_X, y + 8, LIME.r, LIME.g, LIME.b, 0.7));

  const bill = doc.billTo;
  const ship = doc.shipTo;
  const endUser =
    doc.endUser ||
    (doc.shipTo ? { ...doc.shipTo, label: 'End User' as const } : undefined);

  const col1X = MARGIN_X;
  const col2X = 316;
  const contentX1 = 87;
  const contentX2 = 374;

  // Lime accent bars
  if (bill) {
    ops.push(fillRect(78, y - 78, 5.5, 80, LIME.r, LIME.g, LIME.b));
  }
  if (ship) {
    ops.push(fillRect(366, y - 78, 5.5, 80, LIME.r, LIME.g, LIME.b));
  }
  if (endUser) {
    ops.push(fillRect(366, y - 178, 5.5, 84, LIME.r, LIME.g, LIME.b));
  }

  const billBottom = bill ? drawAddressColumn(ops, bill, contentX1, y, 220, true, 'BILL TO:') : y;
  const shipBottom = ship ? drawAddressColumn(ops, ship, contentX2, y, 220, true, 'SHIP TO:') : y;

  // End user under ship column (sample layout)
  let endBottom = shipBottom;
  if (endUser) {
    const endY = y - 97;
    endBottom = drawAddressColumn(ops, endUser, contentX2, endY, 220, true, 'END USER:');
  }

  const afterAddresses = Math.min(billBottom, endBottom) - 16;
  // Meta block sits below addresses (sample ~y 303 from top ≈ PDF 489)
  const metaY = Math.min(afterAddresses, PAGE_H - 303);

  const leftMeta: Array<[string, string | undefined]> = [
    ['Customer#:', doc.customerNumber || doc.accountName],
    ['Account Manager:', doc.accountManager],
    ['Inside Sales Rep:', doc.insideSalesRep],
    ['Title:', doc.title],
  ];
  const rightMeta: Array<[string, string | undefined]> = [
    ['Customer PO#:', doc.customerPo],
    ['Quote #:', doc.quoteNumber],
    ['Payment Terms:', doc.paymentTerms],
  ];

  let ly = metaY;
  for (const [label, value] of leftMeta) {
    if (!value?.trim()) {
      continue;
    }
    ops.push(textAt(col1X, ly, 7, label, true));
    ops.push(textAt(contentX1 + 14, ly, 7, truncate(value, 36)));
    ly -= 11.5;
  }
  let ry = metaY;
  for (const [label, value] of rightMeta) {
    if (!value?.trim() && label !== 'Quote #:') {
      continue;
    }
    ops.push(textAt(col2X + 22, ry, 7, label, true));
    ops.push(textAt(contentX2 + 30, ry, 7, truncate(value || '', 28)));
    ry -= 11.5;
  }

  const afterMeta = Math.min(ly, ry) - 14;
  ops.push(strokeLine(MARGIN_X, afterMeta + 8, PAGE_W - MARGIN_X, afterMeta + 8, LIME.r, LIME.g, LIME.b, 0.7));
  return afterMeta;
}

function drawAddressColumn(
  ops: string[],
  addr: PresidioQuotePdfAddress,
  x: number,
  startY: number,
  _maxW: number,
  withLabel: boolean,
  labelOverride?: string
): number {
  let y = startY;
  if (withLabel) {
    const label =
      labelOverride ||
      (addr.label === 'Bill to' ? 'BILL TO:' : addr.label === 'Ship to' ? 'SHIP TO:' : 'END USER:');
    ops.push(textAt(x - 51, y, 8, label, true));
  }
  if (addr.company) {
    ops.push(textAt(x, y, 8, truncate(addr.company, 40)));
    y -= 11;
  }
  if (addr.name) {
    ops.push(textAt(x, y, 8, truncate(addr.name, 40)));
    y -= 11;
  }
  for (const line of addr.lines || []) {
    if (!line?.trim()) {
      continue;
    }
    ops.push(textAt(x, y, 8, truncate(line, 40)));
    y -= 11;
  }
  if (addr.cityStateZip) {
    ops.push(textAt(x, y, 8, truncate(addr.cityStateZip, 40)));
    y -= 11;
  }
  if (addr.email) {
    y -= 4;
    ops.push(textAt(x, y, 8, truncate(addr.email, 40)));
    y -= 11;
  }
  if (addr.phone) {
    const phone = addr.phone.startsWith('(p)') ? addr.phone : `(p) ${addr.phone}`;
    ops.push(textAt(x, y, 8, truncate(phone, 40)));
    y -= 11;
  }
  return y;
}

function drawTableHeader(ops: string[], y: number): number {
  const h = 17;
  const top = y + 4;
  ops.push(fillRect(MARGIN_X, top - h, PAGE_W - MARGIN_X * 2, h, 0, 0, 0));
  ops.push('1 1 1 rg');
  ops.push(textAt(MARGIN_X + 8, top - 12, 8, '#', true));
  ops.push(textAt(MARGIN_X + 22, top - 12, 8, 'Part #', true));
  ops.push(textAt(MARGIN_X + 106, top - 12, 8, 'Description', true));
  ops.push(textAt(MARGIN_X + 400, top - 12, 8, 'Qty', true));
  ops.push(textAt(MARGIN_X + 440, top - 12, 8, 'Unit', true));
  ops.push(textAt(MARGIN_X + 510, top - 12, 8, 'Ext', true));
  ops.push('0 0 0 rg');
  return top - h - 4;
}

function drawTotals(ops: string[], y: number, subtotal: number, currency: string): number {
  const boxW = 90;
  const boxH = 17;
  const labelX = 398;
  const valueX = 487;
  const rows: Array<[string, string]> = [
    ['Sub Total:', formatMoney(subtotal, currency)],
    ['Grand Total:', formatMoney(subtotal, currency)],
  ];
  let yy = y;
  for (let i = 0; i < rows.length; i++) {
    const [label, value] = rows[i];
    const fill = i === 1;
    if (fill) {
      ops.push(
        fillRect(valueX - 1, yy - 4, boxW, boxH, GRAY_TOTAL.r, GRAY_TOTAL.g, GRAY_TOTAL.b)
      );
    }
    ops.push(strokeRect(valueX - 1, yy - 4, boxW, boxH, 0.5, 0.5, 0.5, 0.34));
    ops.push(strokeRect(labelX - 1, yy - 4, valueX - labelX, boxH, 0.5, 0.5, 0.5, 0.34));
    ops.push(textAt(labelX + 4, yy + 2, 7, label, true));
    ops.push(textAt(valueX + 12, yy + 2, 7, value, true));
    yy -= 18;
  }
  return yy - 4;
}

function drawPageChrome(
  ops: string[],
  doc: PresidioQuotePdfDocument,
  dateStr: string,
  pageNo: number,
  pageCount: number
): void {
  // Watermark (top-right) — PDF coords bottom-left
  // Sample: ~158×187 at x=461, y≈584
  ops.push('q 158.4 0 0 187.2 460.8 583.9 cm /ImWm Do Q');
  // Logo (top-left) — sample ~244×50 at x=34.6, y≈711
  ops.push('q 244.1 0 0 50.4 34.6 710.6 cm /ImLogo Do Q');

  // Header meta (right)
  ops.push(textAt(375, PAGE_H - 48, 15, 'QUOTE:', true));
  ops.push(textAt(454, PAGE_H - 48, 15, truncate(doc.quoteNumber, 18)));
  ops.push(textAt(374, PAGE_H - 64, 7, 'DATE:'));
  ops.push(textAt(453, PAGE_H - 64, 7, dateStr));
  ops.push(textAt(374, PAGE_H - 76, 7, 'PAGE:'));
  ops.push(textAt(453, PAGE_H - 76, 7, `${pageNo} of ${pageCount}`));
}

function drawFooter(ops: string[]): void {
  ops.push(textAt(MARGIN_X, 36, 7, FOOTER_URL));
  // Wrap confidentiality across ~2 lines
  const words = FOOTER_CONFIDENTIAL.split(' ');
  let line = '';
  let y = 24;
  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (next.length > 110) {
      ops.push(textAt(MARGIN_X, y, 6, line));
      y -= 8;
      line = w;
    } else {
      line = next;
    }
  }
  if (line) {
    ops.push(textAt(MARGIN_X, y, 6, line));
  }
}

function wrapText(text: string, max: number): string[] {
  const words = (text || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) {
    return [''];
  }
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const next = cur ? `${cur} ${w}` : w;
    if (next.length > max) {
      if (cur) {
        lines.push(cur);
      }
      cur = w.length > max ? truncate(w, max) : w;
    } else {
      cur = next;
    }
  }
  if (cur) {
    lines.push(cur);
  }
  return lines.slice(0, 4);
}

function fillRect(x: number, y: number, w: number, h: number, r: number, g: number, b: number): string {
  return `${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)} rg ${x} ${y} ${w} ${h} re f 0 0 0 rg`;
}

function strokeRect(
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  g: number,
  b: number,
  lineWidth: number
): string {
  return `${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)} RG ${lineWidth} w ${x} ${y} ${w} ${h} re S 0 0 0 RG`;
}

function strokeLine(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  r: number,
  g: number,
  b: number,
  lineWidth: number
): string {
  return `${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)} RG ${lineWidth} w ${x1} ${y1} m ${x2} ${y2} l S 0 0 0 RG`;
}

function textAt(x: number, y: number, size: number, text: string, bold = false): string {
  const font = bold ? 'F2' : 'F1';
  return `BT /${font} ${size} Tf ${x} ${y} Td (${escapePdf(text)}) Tj ET`;
}

function assemblePdf(contentStreams: string[]): Uint8Array {
  const logoJpeg = decodeJpegBase64(PRESIDIO_QUOTE_PDF_LOGO.jpegBase64);
  const wmJpeg = decodeJpegBase64(PRESIDIO_QUOTE_PDF_WATERMARK.jpegBase64);
  const encoder = new TextEncoder();

  const chunks: Uint8Array[] = [];
  const offsets: number[] = [0];
  let offset = 0;

  const writeText = (s: string, isNewObj = false) => {
    if (isNewObj) {
      offsets.push(offset);
    }
    const bytes = encoder.encode(s);
    chunks.push(bytes);
    offset += bytes.length;
  };
  const writeBinary = (data: Uint8Array) => {
    chunks.push(data);
    offset += data.length;
  };
  const writeImageObj = (width: number, height: number, jpeg: Uint8Array) => {
    writeText(
      `<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>stream\n`
    );
    writeBinary(jpeg);
    writeText('\nendstream\nendobj\n');
  };

  writeText('%PDF-1.4\n');
  // 1 Catalog
  writeText('1 0 obj<< /Type /Catalog /Pages 2 0 R >>endobj\n', true);

  const pageCount = contentStreams.length;
  const pageObjectNumbers: number[] = [];
  // Objects 1..6 fixed; pages start at 7
  let nextObj = 7;
  for (let i = 0; i < pageCount; i++) {
    pageObjectNumbers.push(nextObj);
    nextObj += 2;
  }

  // 2 Pages
  writeText(
    `2 0 obj<< /Type /Pages /Kids [${pageObjectNumbers.map(n => `${n} 0 R`).join(' ')}] /Count ${pageCount} >>endobj\n`,
    true
  );
  writeText('3 0 obj<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>endobj\n', true);
  writeText('4 0 obj<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>endobj\n', true);

  writeText('5 0 obj', true);
  writeImageObj(PRESIDIO_QUOTE_PDF_LOGO.width, PRESIDIO_QUOTE_PDF_LOGO.height, logoJpeg);
  writeText('6 0 obj', true);
  writeImageObj(PRESIDIO_QUOTE_PDF_WATERMARK.width, PRESIDIO_QUOTE_PDF_WATERMARK.height, wmJpeg);

  for (let i = 0; i < pageCount; i++) {
    const pageObj = pageObjectNumbers[i];
    const contentObj = pageObj + 1;
    const streamBytes = encoder.encode(contentStreams[i]);
    writeText(
      `${pageObj} 0 obj<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Contents ${contentObj} 0 R /Resources << /Font << /F1 3 0 R /F2 4 0 R >> /XObject << /ImLogo 5 0 R /ImWm 6 0 R >> >> >>endobj\n`,
      true
    );
    writeText(`${contentObj} 0 obj<< /Length ${streamBytes.length} >>stream\n`, true);
    writeBinary(streamBytes);
    writeText('\nendstream\nendobj\n');
  }

  const xrefStart = offset;
  let xref = `xref\n0 ${offsets.length}\n`;
  xref += '0000000000 65535 f \n';
  for (let i = 1; i < offsets.length; i++) {
    xref += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  }
  xref += `trailer<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;
  writeText(xref);

  const out = new Uint8Array(offset);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.length;
  }
  return out;
}

export function downloadPdfBytes(filename: string, bytes: Uint8Array): void {
  if (typeof document === 'undefined') {
    return;
  }
  const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
